import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, Search, ShieldCheck, Wrench, Gem, RefreshCw, FileCheck, CheckCircle2, Play } from 'lucide-react';
import { processSteps } from '../data/mock';

const stepIcons = [Truck, Search, ShieldCheck, Wrench, Gem, RefreshCw, FileCheck];
const stepImages = [
  'https://images.pexels.com/photos/6169668/pexels-photo-6169668.jpeg',
  'https://images.pexels.com/photos/5726794/pexels-photo-5726794.jpeg',
  'https://images.pexels.com/photos/60504/security-protection-anti-virus-software-60504.jpeg',
  'https://images.pexels.com/photos/162553/keys-workshop-mechanic-tools-162553.jpeg',
  'https://images.pexels.com/photos/4483610/pexels-photo-4483610.jpeg',
  'https://images.pexels.com/photos/802221/pexels-photo-802221.jpeg',
  'https://images.pexels.com/photos/590016/pexels-photo-590016.jpeg',
];

const ProcessPage = () => {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative pt-28 lg:pt-0 min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/1145434/pexels-photo-1145434.jpeg"
            alt="Process"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0a1628]/85" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-4">
              Our Process
            </p>
            <h1 className="text-4xl lg:text-6xl font-light text-white mb-6">
              A Systematic <span className="font-semibold">7-Step Workflow</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              From collection to certification — secure, compliant, and sustainable management of your end-of-life assets.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#4fd1c5] hover:bg-[#38b2ac] text-[#0a1628] px-8 py-4 font-semibold transition-colors">
                Request a Pickup <ArrowRight className="w-5 h-5" />
              </Link>
              <button className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white px-8 py-4 font-semibold transition-colors">
                <Play className="w-5 h-5" /> Watch Overview
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Process Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
              The Journey
            </p>
            <h2 className="text-3xl lg:text-4xl font-light text-[#0a1628] mb-4">
              From <span className="font-semibold">Collection to Certification</span>
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Every asset goes through our rigorous 7-step process, ensuring complete chain of custody, data security, and environmental compliance.
            </p>
          </div>

          {/* Step Navigation */}
          <div className="relative mb-16">
            {/* Progress Line */}
            <div className="absolute top-6 left-0 right-0 h-0.5 bg-gray-200 hidden lg:block">
              <div 
                className="h-full bg-[#4fd1c5] transition-all duration-500"
                style={{ width: `${(activeStep / (processSteps.length - 1)) * 100}%` }}
              />
            </div>
            
            {/* Step Buttons */}
            <div className="grid grid-cols-7 gap-2 lg:gap-4">
              {processSteps.map((step, i) => {
                const Icon = stepIcons[i];
                const isActive = i === activeStep;
                const isPast = i < activeStep;
                
                return (
                  <button
                    key={i}
                    onClick={() => setActiveStep(i)}
                    className="flex flex-col items-center text-center group"
                  >
                    <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center transition-all mb-3 ${
                      isActive 
                        ? 'bg-[#4fd1c5] text-[#0a1628] scale-110 shadow-lg' 
                        : isPast 
                          ? 'bg-[#0a1628] text-white' 
                          : 'bg-gray-100 text-gray-400 group-hover:bg-gray-200'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className={`text-xs font-semibold hidden lg:block ${
                      isActive ? 'text-[#4fd1c5]' : isPast ? 'text-[#0a1628]' : 'text-gray-400'
                    }`}>
                      Step {i + 1}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active Step Content */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative overflow-hidden aspect-[4/3]">
              <img 
                src={stepImages[activeStep]}
                alt={processSteps[activeStep].title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-6 left-6 bg-[#4fd1c5] text-[#0a1628] px-4 py-2 font-bold">
                Step {String(activeStep + 1).padStart(2, '0')}
              </div>
            </div>
            
            {/* Content */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                {React.createElement(stepIcons[activeStep], { className: 'w-8 h-8 text-[#4fd1c5]' })}
                <span className="text-[#4fd1c5] text-sm font-semibold uppercase tracking-wider">
                  Step {activeStep + 1} of 7
                </span>
              </div>
              <h3 className="text-3xl lg:text-4xl font-semibold text-[#0a1628] mb-4">
                {processSteps[activeStep].title}
              </h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {processSteps[activeStep].description}
              </p>
              
              {/* Navigation */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                  disabled={activeStep === 0}
                  className={`px-6 py-3 font-semibold transition-colors ${
                    activeStep === 0 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-gray-100 text-[#0a1628] hover:bg-gray-200'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => setActiveStep(Math.min(processSteps.length - 1, activeStep + 1))}
                  disabled={activeStep === processSteps.length - 1}
                  className={`px-6 py-3 font-semibold transition-colors ${
                    activeStep === processSteps.length - 1 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-[#0a1628] text-white hover:bg-[#4fd1c5] hover:text-[#0a1628]'
                  }`}
                >
                  Next Step
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 lg:py-28 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
                Why Our Process
              </p>
              <h2 className="text-3xl lg:text-4xl font-light text-[#0a1628] mb-8">
                What Sets Us <span className="font-semibold">Apart</span>
              </h2>
              <div className="space-y-6">
                {[
                  { title: 'Chain of Custody', desc: 'Complete tracking and documentation from pickup to final disposition with real-time visibility.' },
                  { title: 'Data Security First', desc: 'Certified data destruction methods with compliance certificates and audit trails.' },
                  { title: 'Zero Landfill Commitment', desc: 'We recover up to 98% of materials, ensuring minimal environmental impact.' },
                  { title: 'Full Transparency', desc: 'Digital reporting portal with access to certificates, manifests, and recovery reports.' },
                ].map((feature, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-[#4fd1c5] mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-[#0a1628] mb-1">{feature.title}</h4>
                      <p className="text-gray-500">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/3735218/pexels-photo-3735218.jpeg"
                alt="Our Process"
                className="w-full aspect-square object-cover"
              />
              <div className="absolute bottom-0 right-0 bg-[#0a1628] p-8 text-white">
                <div className="text-4xl font-bold text-[#4fd1c5] mb-1">98%</div>
                <div className="text-sm">Material Recovery Rate</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#0a1628]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-light text-white mb-6">
            Experience the <span className="font-semibold">Grenew Difference</span>
          </h2>
          <p className="text-xl text-gray-400 mb-10">
            Start your journey towards sustainable asset management today.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-[#4fd1c5] hover:bg-[#38b2ac] text-[#0a1628] px-10 py-5 font-semibold text-lg transition-colors">
            Request a Pickup <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default ProcessPage;
